import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import {
  downloadLeaveExport,
  useLeaveExportCount,
  useLeaveExportOptions,
  type LeaveExportPageFilter,
} from '@/products/leave/features/use-leave-admin';

const REPORTS = [
  { kind: 'requests' as const, label: 'Anträge' },
  { kind: 'balances' as const, label: 'Salden' },
  { kind: 'overtime' as const, label: 'Überstunden' },
];

export function LeaveAdminExportPage() {
  usePageTitle('Export');
  const today = new Date();
  const currentYear = today.getFullYear();
  const [from, setFrom] = useState(`${currentYear}-01-01`);
  const [to, setTo] = useState(`${currentYear}-12-31`);
  const [departmentId, setDepartmentId] = useState('');
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const options = useLeaveExportOptions();
  const year = Number(from.slice(0, 4));
  const rangeReady =
    /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && to >= from;
  const users = (options.data?.users ?? []).filter(
    (user) => userId === user.id || !departmentId || user.departmentId === departmentId,
  );

  async function onDownload(
    kind: (typeof REPORTS)[number]['kind'],
    format: 'csv' | 'xlsx' | 'datev' | 'loga',
  ) {
    if (!rangeReady) {
      return;
    }
    const key = `${kind}-${format}`;
    setBusy(key);
    try {
      const result = await downloadLeaveExport({
        year,
        from,
        to,
        departmentId: departmentId || undefined,
        userId: userId || undefined,
        format,
        kind,
      });
      if (result.skipped.length > 0) {
        toast.warning(
          `Ohne Personalnummer nicht in der Datei: ${result.skipped.map((person) => person.displayName).join(', ')}`,
        );
      } else {
        toast.success('Export wurde heruntergeladen.');
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Der Export konnte nicht erstellt werden.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Export"
        description="Anträge, Salden und Überstunden filtern, die Trefferzahl prüfen und herunterladen. DATEV und LOGA sind Dateien, keine Live-Schnittstelle. Salden gelten für das Jahr des Startdatums."
      />
      {options.isError ? (
        <p className="text-sm text-destructive">
          {options.error instanceof ApiRequestError
            ? options.error.message
            : 'Der Export konnte nicht geladen werden.'}
        </p>
      ) : (
        <div className="space-y-6">
          <div className={`grid gap-4 ${leavePanelClassName} sm:grid-cols-2`}>
            <div>
              <Label htmlFor="export-from">Von</Label>
              <Input
                id="export-from"
                type="date"
                className="mt-2"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="export-to">Bis</Label>
              <Input
                id="export-to"
                type="date"
                className="mt-2"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="export-department">Abteilung</Label>
              <Select
                value={departmentId || 'none'}
                onValueChange={(value) => {
                  setDepartmentId(value === 'none' ? '' : value);
                  setUserId('');
                }}
              >
                <SelectTrigger id="export-department" className="mt-2 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Alle Abteilungen</SelectItem>
                  {options.data?.departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="export-user">Person</Label>
              <Select
                value={userId || 'none'}
                onValueChange={(value) => setUserId(value === 'none' ? '' : value)}
              >
                <SelectTrigger id="export-user" className="mt-2 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Alle Personen</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {rangeReady ? null : (
            <p className="text-sm text-destructive">Das Enddatum liegt vor dem Startdatum.</p>
          )}
          <div className="space-y-4">
            {REPORTS.map((report) => (
              <ExportReport
                key={report.kind}
                label={report.label}
                filter={{
                  year: Number.isInteger(year) ? year : currentYear,
                  from,
                  to,
                  departmentId: departmentId || undefined,
                  userId: userId || undefined,
                  kind: report.kind,
                }}
                enabled={rangeReady}
                busy={busy}
                onDownload={(format) => void onDownload(report.kind, format)}
              />
            ))}
          </div>
          <section className={leavePanelClassName}>
            <h2 className="text-lg font-semibold">Lohn</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Genehmigte Fehlzeiten im gewählten Zeitraum. Personen ohne Personalnummer fehlen in
              der Datei und werden danach genannt. Krankkommentare bleiben leer.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy !== null || !rangeReady}
                onClick={() => void onDownload('requests', 'datev')}
              >
                {busy === 'requests-datev' ? 'Wird erstellt …' : 'DATEV herunterladen'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy !== null || !rangeReady}
                onClick={() => void onDownload('requests', 'loga')}
              >
                {busy === 'requests-loga' ? 'Wird erstellt …' : 'LOGA herunterladen'}
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function ExportReport({
  label,
  filter,
  enabled,
  busy,
  onDownload,
}: {
  label: string;
  filter: LeaveExportPageFilter;
  enabled: boolean;
  busy: string | null;
  onDownload: (format: 'csv' | 'xlsx') => void;
}) {
  const count = useLeaveExportCount(filter, enabled);
  const total = count.data?.total;

  return (
    <section className={leavePanelClassName}>
      <h2 className="text-lg font-semibold">{label}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {enabled && count.isSuccess && total != null
          ? `${total.toLocaleString('de-DE')} Treffer`
          : enabled && count.isError
            ? 'Die Trefferzahl konnte nicht geladen werden.'
            : 'Treffer werden gezählt …'}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={busy !== null || !enabled}
          onClick={() => onDownload('csv')}
        >
          {busy === `${filter.kind}-csv` ? 'Wird erstellt …' : 'CSV herunterladen'}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy !== null || !enabled}
          onClick={() => onDownload('xlsx')}
        >
          {busy === `${filter.kind}-xlsx` ? 'Wird erstellt …' : 'Excel herunterladen'}
        </Button>
      </div>
    </section>
  );
}
